import React, { Fragment, useState, useEffect, useContext } from 'react';
import { ProjectContext } from '../../../context/Project';
import { useNavigate } from 'react-router-dom';
import useSolution from '../../../hooks/useSolution';
import { Button, Modal, ModalBody, Toast, ToastBody } from 'reactstrap';
import { APPConstants } from '../../../constants/app';
import { solutionBuilder } from '../../../builders/solution';
import { projectBuilder } from '../../../builders/project';
import { mediaFileBuilder } from '../../../builders/project/mediaFile';
import { materialDetailBuilder } from '../../../builders/project/materialDetail';
import { PixelContext } from '../../../context/Pixel';

function CreateButton(props) {
    const navigate = useNavigate();
    const solutionManager = useSolution();
    const solution = solutionBuilder();
    const createProject = projectBuilder();
    const { project, clearProjectContext } = useContext(ProjectContext);
    const { trackAnalyticsEvent, Events } = useContext(PixelContext);

    const [showError, setShowError] = useState(false);

    useEffect(() => {
		if (solutionManager.solution) {
            // Clear context and navigate away to success page
            clearProjectContext();
			navigateOnSuccess();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [solutionManager.solution]);

    useEffect(() => {
        if (solutionManager.error) {
            setShowError(true);
        } else {
            setShowError(false);
        }
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [solutionManager.error]);

    const shouldEnableButton = () => {
        return project.contractorId && !solutionManager.loading;
    }

    const handleCreateSolution = () => {
		trackAnalyticsEvent(Events.PROJECT_CREATE_CLICK, {
            contractorId: project.contractorId
        });
        solutionManager.create(createSolution());
    }

    const createSolution = () => {
        const builtProject = createProject.setDescription(project.description)
            .setProjectTypeId(project.type?.id)
            .setLaborBudget(project.laborBudget)
            .setFloorArea(project.area)
            .setFloorAreaMeasurementUnitId(APPConstants.MATERIAL_FLOOR_MEASUREMENT_UNIT_ID)
            .setCreateProjectMaterialDetails(buildMaterialDetails())
            .setCreateProjectMediaFiles(buildMediaFiles())
            .build();

        return solution.setAdministrativeAreaId(project.location?.administrativeAreaId)
            .setAdministrativeAreaName(project.location?.administrativeAreaName)
            .setLocality(project.location?.name)
            .setPostalCode(project.postalCode)
            .setCountryId(APPConstants.COUNTRY_ID)
            .setTotalBudget(project.totalBudget)
            .setCurrencyId(APPConstants.CURRENCY_ID)
            .setContractorId(project.contractorId)
            .setCreateProjects([builtProject])
            .build();
    }

    const buildMaterialDetails = () => {
        const builtMaterials = [];

        for (let materialId in project.materials) {
            const material = project.materials[materialId];
            const materialDetail = materialDetailBuilder();

            materialDetail.setMaterialId(material.materialId)
                .setMaterialCategoryId(material.materialCategoryId)
                .setQuantity(material.quantity)
                .setPrice(material.price)
                .build();

            builtMaterials.push(materialDetail);
        }

        return builtMaterials;
    }

    const buildMediaFiles = () => {
        const builtMediaFiles = [];

        if (project.spacePhotos?.length > 0) {
            project.spacePhotos.forEach(function(spacePhoto) {
                const mediaFile = mediaFileBuilder();
                mediaFile.setFileName(spacePhoto.uploadedFile)
                    .setDescription(spacePhoto.name)
                    .build();

                builtMediaFiles.push(mediaFile);
            });
        }

        return builtMediaFiles;
    }

    const navigateOnSuccess = () => {
        navigate(
            '/project/create/success',
            {state: {solution: solutionManager.solution}}
        );
    }

    return (
        <Fragment>
            {
                <Button disabled={!shouldEnableButton()}
                    onClick={handleCreateSolution}
                    color="primary"
                    size="lg"
                    {...props}
                >
                    {props.text ? props.text : "Create Project"}
                </Button>
            }
            {
                <Modal className='project-create-loading' isOpen={solutionManager.loading} fullscreen>
                    <ModalBody className='loading-body'>
                        <svg className="spinner" viewBox="0 0 50 50">
                            <circle className="path" cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
                        </svg>
                        <div className='loading-text'>
                            <div className='text-center'>
                                <p className='text-mini-info text-white'>
                                    Please wait
                                </p>
                                <p className='text-mini-info text-white'>
                                    We are finishing getting things ready for your project and contacting the contractor...
                                </p>
                            </div>
                        </div>
                    </ModalBody>
                </Modal>
            }
            {
                <Toast isOpen={showError} className='builde-cont project-create-error bg-danger position-fixed top-0 start-0 px-0'>
                    <div className="d-flex text-white ">
                        <ToastBody className='w-100'>
                            <div className='text-center ms-3'>
                                <h5>We have a little problem</h5>
                                <h6 className='mt-3'>
                                    The project couldn't be created at this time. Please make sure we've got all data right and try again.
                                </h6>
                            </div>
                        </ToastBody>
                        <div className='pt-2'>
                            <Button className='btn-close btn-close-white me-2 m-auto' onClick={() => { setShowError(false) }}/>
                        </div>                        
                    </div>
                </Toast>
            }
        </Fragment>
    );
}

export default CreateButton;